/**
 * Phase 2d — AI-assisted census upload.
 *
 * Replaces (tab-wraps) the Phase 1.5d manual-paste batch-invite panel. Admin
 * uploads CSV/XLSX, Claude suggests a column mapping, the admin reviews
 * mapping + row-level normalised data, then commits. Manual paste is kept as
 * a fallback tab so nothing regresses.
 *
 * Scope rules:
 *   - Backend lives at /api/employer/census/* and is gated by laguna_admin.
 *   - Admin console aesthetic (paper/ink/accent, Fraunces + Instrument Sans
 *     + JetBrains Mono). No Tailwind utility classes — custom CSS in
 *     Admin.css only.
 *   - All side-effects fire a `track()` event per brief.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    censusCommit,
    censusMap,
    censusParse,
    censusPasteCommit,
    censusSampleCsvUrl,
    censusSampleXlsxUrl,
    censusUndoCommit,
    censusValidate,
} from "../lib/admin";
import { track } from "../lib/analytics";

const MAX_FILE_BYTES = 5 * 1024 * 1024;            // 5 MB — matches backend
const ACCEPT_EXTENSIONS = [".csv", ".xlsx"];
const CONFIDENCE_STRONG = 0.85;
const CONFIDENCE_PLAUSIBLE = 0.6;

/* -------------------------------------------------------------------- *
 * Tab chrome — plain CSS pills, admin-console aesthetic.
 * -------------------------------------------------------------------- */
function TabBar({ active, onChange }) {
    const { t } = useTranslation();
    const items = [
        { id: "upload", label: t("admin.census.tabs.upload") },
        { id: "paste", label: t("admin.census.tabs.paste") },
    ];
    return (
        <div className="cu-tabs" role="tablist" data-testid="census-tabs">
            {items.map((it) => (
                <button
                    key={it.id}
                    role="tab"
                    type="button"
                    aria-selected={active === it.id}
                    className={`cu-tab${active === it.id ? " is-active" : ""}`}
                    onClick={() => onChange(it.id)}
                    data-testid={`census-tab-${it.id}`}
                >
                    {it.label}
                </button>
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Manual paste — updated to the Phase 2e expanded schema. Header row is
 * auto-detected and skipped. Paste format:
 *   first_name,last_name,email,dob[,classification][,locale]
 * Legacy age-only rows still work ("dob" that's just a 2-digit age) but
 * the row is flagged with a warning. Same conservative validation the
 * backend paste-CSV helper applies.
 * -------------------------------------------------------------------- */
function parseCsvRows(raw) {
    const lines = (raw || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const rows = [];
    const bad = [];
    const legacyAge = [];
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (i === 0 && /first[_ ]?name|email|dob|age/i.test(line) && !/@/.test(line)) {
            continue;
        }
        const parts = line.split(",").map((c) => c.trim()).filter((x) => x !== "");
        if (parts.length < 4) {
            bad.push({ line: i + 1, raw: line, reason: "need first,last,email,dob" });
            continue;
        }
        const [first_name, last_name, email, dobOrAge, classification, locale] = parts;
        if (!email.includes("@")) {
            bad.push({ line: i + 1, raw: line, reason: "bad email" });
            continue;
        }
        // DOB path — YYYY-MM-DD or MM/DD/YYYY etc. Compute age client-side;
        // backend re-validates anyway.
        let age = null;
        let dob = null;
        const asInt = parseInt(dobOrAge, 10);
        const isoMatch = /^\d{4}-\d{1,2}-\d{1,2}$/.test(dobOrAge);
        const usMatch = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dobOrAge);
        if (isoMatch || usMatch) {
            const parts2 = isoMatch
                ? dobOrAge.split("-").map((n) => parseInt(n, 10))
                : (() => {
                    const p = dobOrAge.split("/").map((n) => parseInt(n, 10));
                    return [p[2], p[0], p[1]];   // YYYY, M, D
                })();
            const [y, m, d] = parts2;
            const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
            if (!Number.isNaN(dt.getTime())) {
                dob = `${y.toString().padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const today = new Date();
                age = today.getUTCFullYear() - y - (((today.getUTCMonth() + 1) < m) || ((today.getUTCMonth() + 1) === m && today.getUTCDate() < d) ? 1 : 0);
            }
        } else if (!Number.isNaN(asInt) && asInt >= 16 && asInt <= 99) {
            age = asInt;
            legacyAge.push(i + 1);
        }
        if (age === null) {
            bad.push({ line: i + 1, raw: line, reason: "bad dob/age" });
            continue;
        }
        rows.push({
            first_name,
            last_name,
            email,
            dob: dob || undefined,
            age,
            classification: (classification || "non_tobacco").toLowerCase(),
            locale: (locale || "en").toLowerCase(),
        });
    }
    return { rows, bad, legacyAge };
}

function PasteFallback({ applicationId, onDone }) {
    const { t } = useTranslation();
    const [raw, setRaw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [commit, setCommit] = useState(null);      // shape: {appended, skipped, errors, upload_id, undo_expires_at}
    const [err, setErr] = useState(null);
    const [localBad, setLocalBad] = useState([]);
    const [legacyAgeLines, setLegacyAgeLines] = useState([]);

    const onSubmit = async () => {
        setErr(null);
        setCommit(null);
        const { rows, bad, legacyAge } = parseCsvRows(raw);
        setLocalBad(bad);
        setLegacyAgeLines(legacyAge || []);
        if (rows.length === 0) {
            setErr(t("admin.census.paste.noRows"));
            return;
        }
        setSubmitting(true);
        try {
            // Phase 2e §4 — paste now flows through /employer/census/paste_commit
            // and inherits the 30 s undo window. The backend accepts both
            // dob (preferred) and age (legacy) per row.
            const payload = rows.map((r) => ({
                first_name: r.first_name,
                last_name: r.last_name,
                email: r.email,
                dob: r.dob || null,
                age: r.dob ? null : r.age,
                classification: r.classification,
                locale: r.locale,
            }));
            const res = await censusPasteCommit(applicationId, payload);
            setCommit(res);
            track("admin_census_paste_commit", {
                applicationId,
                total: payload.length,
                appended: res?.appended || 0,
                skipped: res?.skipped || 0,
                errors: (res?.errors || []).length,
            });
            if ((res?.appended || 0) > 0 && onDone) onDone();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(
                typeof d === "object"
                    ? d?.message || t("admin.census.paste.submitFailed")
                    : d || t("admin.census.paste.submitFailed"),
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Successful commit → render the same CommitResult component the upload
    // tab uses, so the countdown + Undo strip is shared.
    if (commit && (commit.appended || 0) > 0) {
        return (
            <CommitResult
                commit={commit}
                applicationId={applicationId}
                onDoneAnother={() => { setCommit(null); setRaw(""); setErr(null); setLocalBad([]); setLegacyAgeLines([]); }}
                onClose={() => { setCommit(null); setRaw(""); setErr(null); setLocalBad([]); setLegacyAgeLines([]); }}
                onUndone={() => {
                    // After undo, drop the user back to the paste textarea
                    // so they can adjust and re-submit. setCommit(null) hides
                    // the countdown strip; the textarea is preserved for edits.
                    setCommit(null);
                }}
            />
        );
    }

    return (
        <div className="cu-paste" data-testid="cu-paste">
            <div
                className="batch-help"
                style={{ fontStyle: "normal", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.72rem", letterSpacing: "0.08em" }}
            >
                {t("admin.census.paste.format")}{" "}
                <code>first_name,last_name,email,dob[,classification][,locale]</code>
            </div>
            <textarea
                className="batch-invite-textarea"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={"Sarah,Lee,sarah@acme.com,1990-03-15\nMiguel,Nguyen,miguel@acme.com,1982-11-04,tobacco\nJane,Doe,jane@acme.com,32"}
                data-testid="batch-invite-textarea"
                disabled={submitting}
            />
            {err && (
                <div className="review-err" data-testid="batch-invite-err" style={{ marginTop: "0.75rem" }}>{err}</div>
            )}
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
                <button
                    className="admin-btn primary"
                    onClick={onSubmit}
                    disabled={submitting || !raw.trim()}
                    data-testid="batch-invite-submit"
                >
                    {submitting ? t("admin.census.paste.sending") : t("admin.census.paste.sendCta")}
                </button>
                {raw && !submitting && (
                    <button
                        className="admin-btn secondary"
                        onClick={() => { setRaw(""); setCommit(null); setErr(null); setLocalBad([]); setLegacyAgeLines([]); }}
                        data-testid="batch-invite-clear"
                    >
                        {t("admin.census.paste.clear")}
                    </button>
                )}
            </div>
            {legacyAgeLines.length > 0 && (
                <div className="cu-legacy-age-warn" data-testid="batch-invite-legacy-age-warn">
                    {t("admin.census.paste.legacyAgeWarn", { lines: legacyAgeLines.join(", ") })}
                </div>
            )}
            {localBad.length > 0 && (
                <ul className="batch-invite-errors" data-testid="batch-invite-local-errors">
                    {localBad.map((b, i) => (
                        <li key={i}>{t("admin.census.paste.lineLabel", { n: b.line })}: {b.reason} — <em>{b.raw}</em></li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Drop zone (Step 1 — Parse).
 * -------------------------------------------------------------------- */
function DropZone({ onFile, busy, error }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleFiles = (files) => {
        if (!files || files.length === 0) return;
        const f = files[0];
        const lower = (f.name || "").toLowerCase();
        if (!ACCEPT_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
            onFile(null, t("admin.census.upload.badType"));
            return;
        }
        if (f.size > MAX_FILE_BYTES) {
            onFile(null, t("admin.census.upload.tooLarge"));
            return;
        }
        onFile(f, null);
    };

    return (
        <div
            className={`cu-dropzone${dragging ? " is-dragging" : ""}${busy ? " is-busy" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                if (busy) return;
                handleFiles(e.dataTransfer.files);
            }}
            onClick={() => !busy && inputRef.current?.click()}
            role="button"
            tabIndex={0}
            data-testid="census-dropzone"
        >
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT_EXTENSIONS.join(",")}
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="census-file-input"
            />
            <div className="cu-dropzone-icon" aria-hidden="true">↑</div>
            <div className="cu-dropzone-title">
                {busy ? t("admin.census.upload.parsing") : t("admin.census.upload.dropTitle")}
            </div>
            {!busy && (
                <>
                    <div className="cu-dropzone-sub">{t("admin.census.upload.dropSub")}</div>
                    <div className="cu-dropzone-limits mono">
                        {t("admin.census.upload.limits")}
                    </div>
                    <div
                        className="cu-sample-links mono"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="cu-sample-links"
                    >
                        {t("admin.census.upload.sampleLabel")}{" "}
                        <a
                            href={censusSampleCsvUrl()}
                            download
                            data-testid="cu-sample-csv"
                            onClick={() => track("admin_census_sample_download", { format: "csv" })}
                        >
                            CSV
                        </a>
                        {" · "}
                        <a
                            href={censusSampleXlsxUrl()}
                            download
                            data-testid="cu-sample-xlsx"
                            onClick={() => track("admin_census_sample_download", { format: "xlsx" })}
                        >
                            XLSX
                        </a>
                    </div>
                </>
            )}
            {error && <div className="review-err" style={{ marginTop: "0.75rem" }} data-testid="census-upload-err">{error}</div>}
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Mapping grid (Step 2).
 * -------------------------------------------------------------------- */
function confidenceBucket(c) {
    if (c >= CONFIDENCE_STRONG) return "strong";
    if (c >= CONFIDENCE_PLAUSIBLE) return "plausible";
    return "uncertain";
}

function MappingGrid({
    parse,
    mapping,           // { uploadedHeader: { canonical_field, confidence, reason } }
    canonicalSchema,
    autoApplied,
    onChange,          // (uploadedHeader, canonicalOrEmpty) => void
    rememberMapping,
    setRememberMapping,
    onContinue,
    onBack,
    onRemap,
    validating,
}) {
    const { t } = useTranslation();
    const canonicalList = Object.keys(canonicalSchema);
    const requiredFields = canonicalList.filter((k) => canonicalSchema[k]?.required);

    // Header → 3 sample values from parse.sample_rows
    const headerSamples = useMemo(() => {
        const out = {};
        (parse?.headers || []).forEach((h, col) => {
            const samples = [];
            for (const row of parse?.sample_rows || []) {
                const v = row[col];
                if (v !== undefined && v !== null && v !== "") samples.push(String(v));
                if (samples.length >= 3) break;
            }
            out[h] = samples;
        });
        return out;
    }, [parse]);

    // Detect duplicate canonical assignments (two headers both mapped to `email` etc.)
    const usedCounts = useMemo(() => {
        const counts = {};
        Object.values(mapping).forEach((entry) => {
            const cf = entry?.canonical_field;
            if (cf) counts[cf] = (counts[cf] || 0) + 1;
        });
        return counts;
    }, [mapping]);

    // Which required canonicals are not yet mapped?
    const unmappedRequired = useMemo(() => {
        const mapped = new Set(
            Object.values(mapping)
                .map((m) => m?.canonical_field)
                .filter(Boolean),
        );
        return requiredFields.filter((f) => !mapped.has(f));
    }, [mapping, requiredFields]);

    const canContinue = unmappedRequired.length === 0 && !validating;

    return (
        <div className="cu-mapping" data-testid="census-mapping">
            <div className="cu-step-head">
                <div className="cu-step-label mono">{t("admin.census.map.label")}</div>
                <h4>{t("admin.census.map.title")}</h4>
                <p className="cu-step-sub">
                    {autoApplied ? t("admin.census.map.autoApplied") : t("admin.census.map.sub")}
                </p>
            </div>

            <div className="cu-mapping-grid" role="table">
                <div className="cu-mapping-head" role="row">
                    <div role="columnheader">{t("admin.census.map.uploadedCol")}</div>
                    <div role="columnheader">{t("admin.census.map.samplesCol")}</div>
                    <div role="columnheader">{t("admin.census.map.canonicalCol")}</div>
                    <div role="columnheader">{t("admin.census.map.confCol")}</div>
                </div>
                {(parse?.headers || []).map((h) => {
                    const entry = mapping[h] || { canonical_field: null, confidence: 0, reason: "" };
                    const bucket = confidenceBucket(entry.confidence || 0);
                    const cf = entry.canonical_field || "";
                    const dupFlag = cf && usedCounts[cf] > 1;
                    return (
                        <div className="cu-mapping-row" role="row" key={h} data-testid={`cu-map-row-${h}`}>
                            <div role="cell" className="cu-mapping-header-name">
                                <span className="mono">{h}</span>
                            </div>
                            <div role="cell" className="cu-mapping-samples">
                                {(headerSamples[h] || []).slice(0, 3).map((s, i) => (
                                    <span key={i} className="cu-sample-pill">{s}</span>
                                ))}
                                {(!headerSamples[h] || headerSamples[h].length === 0) && (
                                    <span className="cu-sample-empty">—</span>
                                )}
                            </div>
                            <div role="cell">
                                <select
                                    className="cu-select"
                                    value={cf}
                                    onChange={(e) => onChange(h, e.target.value)}
                                    data-testid={`cu-map-select-${h}`}
                                >
                                    <option value="">{t("admin.census.map.skipOption")}</option>
                                    {canonicalList.map((c) => (
                                        <option key={c} value={c}>
                                            {c}{canonicalSchema[c]?.required ? " *" : ""}
                                        </option>
                                    ))}
                                </select>
                                {dupFlag && (
                                    <div className="cu-dup-warn mono" data-testid={`cu-dup-warn-${h}`}>
                                        {t("admin.census.map.duplicateMap")}
                                    </div>
                                )}
                                {entry.reason && (
                                    <div className="cu-reason" title={entry.reason}>
                                        {entry.reason}
                                    </div>
                                )}
                            </div>
                            <div role="cell">
                                {cf ? (
                                    <span className={`cu-badge cu-badge-${bucket}`} data-testid={`cu-conf-${h}`}>
                                        {Math.round((entry.confidence || 0) * 100)}%
                                        <span className="cu-badge-sub">
                                            {t(`admin.census.map.conf.${bucket}`)}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="cu-badge cu-badge-skip">
                                        {t("admin.census.map.conf.skip")}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {unmappedRequired.length > 0 && (
                <div className="review-err" data-testid="cu-unmapped-required">
                    <strong>{t("admin.census.map.blockedTitle")}</strong>
                    <ul style={{ margin: "0.5rem 0 0 1rem", padding: 0 }}>
                        {unmappedRequired.map((f) => <li key={f}><code>{f}</code></li>)}
                    </ul>
                </div>
            )}

            <label className="cu-remember" data-testid="cu-remember-mapping">
                <input
                    type="checkbox"
                    checked={rememberMapping}
                    onChange={(e) => setRememberMapping(e.target.checked)}
                />
                <span>{t("admin.census.map.remember")}</span>
            </label>

            <div className="cu-step-actions">
                <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={onBack}
                    data-testid="cu-map-back"
                >
                    ←︎ {t("admin.census.common.back")}
                </button>
                <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={onRemap}
                    data-testid="cu-map-remap"
                >
                    {t("admin.census.map.remap")}
                </button>
                <button
                    type="button"
                    className="admin-btn primary"
                    disabled={!canContinue}
                    onClick={onContinue}
                    data-testid="cu-map-continue"
                >
                    {validating ? t("admin.census.common.validating") : t("admin.census.map.continueCta")}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Review grid (Step 3).
 * -------------------------------------------------------------------- */
const REVIEW_COLS = [
    "first_name",
    "last_name",
    "email",
    "dob",
    "age",
    "hire_date",
    "annual_salary",
    "gender",
    "department",
    "employee_id",
    "ssn_last4",
    "classification",
    "locale",
];

function ReviewGrid({
    validate,              // ValidateOut
    overrides,             // { [index]: {field: value} }
    setOverride,           // (index, field, value) => void
    actions,               // { [index]: "include"|"exclude"|"overwrite" }
    setAction,             // (index, action) => void
    onRevalidate,
    onCommit,
    onBack,
    revalidating,
    committing,
}) {
    const { t } = useTranslation();
    const rows = validate.rows || [];
    const summary = validate.summary || {};

    const includeCount = rows.reduce((acc, _, i) => acc + ((actions[i] || inferDefaultAction(_, i, validate)) === "include" ? 1 : 0), 0);
    const overwriteCount = rows.reduce((acc, _, i) => acc + (actions[i] === "overwrite" ? 1 : 0), 0);
    const excludeCount = rows.length - includeCount - overwriteCount;

    const anyDirty = Object.keys(overrides).length > 0;

    return (
        <div className="cu-review" data-testid="census-review">
            <div className="cu-step-head">
                <div className="cu-step-label mono">{t("admin.census.review.label")}</div>
                <h4>{t("admin.census.review.title")}</h4>
                <p className="cu-step-sub">{t("admin.census.review.sub", { total: rows.length })}</p>
            </div>

            <div className="cu-summary-strip" data-testid="cu-summary-strip">
                <span className="cu-stat"><b>{summary.total ?? rows.length}</b> {t("admin.census.review.total")}</span>
                <span className="cu-stat cu-stat-good"><b>{summary.valid ?? 0}</b> {t("admin.census.review.valid")}</span>
                <span className="cu-stat cu-stat-warn"><b>{summary.with_warnings ?? 0}</b> {t("admin.census.review.warnings")}</span>
                <span className="cu-stat cu-stat-err"><b>{summary.with_errors ?? 0}</b> {t("admin.census.review.errors")}</span>
                <span className="cu-stat cu-stat-dup"><b>{summary.duplicates ?? 0}</b> {t("admin.census.review.duplicates")}</span>
            </div>

            <div className="cu-table-wrap">
                <table className="cu-table" data-testid="cu-review-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            {REVIEW_COLS.map((c) => <th key={c}>{c.replace("_", " ")}</th>)}
                            <th>{t("admin.census.review.statusCol")}</th>
                            <th>{t("admin.census.review.actionCol")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const currentAction = actions[i] ?? inferDefaultAction(r, i, validate);
                            const hasErrors = (r._errors || []).length > 0;
                            const dup = r._duplicate_of;
                            const isIntra = dup && dup.source === "intra_file";
                            const isCross = dup && dup.source === "existing_roster";
                            return (
                                <tr
                                    key={i}
                                    className={
                                        `cu-row ${currentAction === "exclude" ? "is-excluded" : ""} ` +
                                        `${hasErrors ? "is-error" : ""} ${dup ? "is-dup" : ""}`
                                    }
                                    data-testid={`cu-review-row-${i}`}
                                >
                                    <td className="cu-rownum mono">{i + 1}</td>
                                    {REVIEW_COLS.map((c) => (
                                        <EditableCell
                                            key={c}
                                            row={r}
                                            field={c}
                                            override={overrides[i]?.[c]}
                                            onEdit={(v) => setOverride(i, c, v)}
                                            rowIndex={i}
                                        />
                                    ))}
                                    <td className="cu-status-cell">
                                        <RowStatus row={r} />
                                    </td>
                                    <td className="cu-action-cell">
                                        <ActionPicker
                                            rowIndex={i}
                                            current={currentAction}
                                            hasErrors={hasErrors}
                                            isIntra={isIntra}
                                            isCross={isCross}
                                            onChange={(a) => setAction(i, a)}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="cu-commit-strip" data-testid="cu-commit-strip">
                <span className="mono">
                    {t("admin.census.review.committing", {
                        include: includeCount,
                        overwrite: overwriteCount,
                        exclude: excludeCount,
                    })}
                </span>
            </div>

            <div className="cu-step-actions">
                <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={onBack}
                    data-testid="cu-review-back"
                >
                    ←︎ {t("admin.census.common.back")}
                </button>
                {anyDirty && (
                    <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={onRevalidate}
                        disabled={revalidating}
                        data-testid="cu-review-revalidate"
                    >
                        {revalidating
                            ? t("admin.census.common.validating")
                            : t("admin.census.review.revalidate")}
                    </button>
                )}
                <button
                    type="button"
                    className="admin-btn primary"
                    onClick={onCommit}
                    disabled={committing || includeCount + overwriteCount === 0}
                    data-testid="cu-review-commit"
                >
                    {committing
                        ? t("admin.census.review.committing_now")
                        : t("admin.census.review.commitCta", { n: includeCount + overwriteCount })}
                </button>
            </div>
        </div>
    );
}

function inferDefaultAction(row, _idx, validate) {
    if ((row._errors || []).length > 0) return "exclude";
    const dup = row._duplicate_of;
    if (dup && dup.source === "intra_file") return "exclude";
    if (dup && dup.source === "existing_roster") return "exclude";
    return "include";
}

function EditableCell({ row, field, override, onEdit, rowIndex }) {
    const displayVal = override !== undefined ? override : (row[field] ?? "");
    const cellErrors = (row._errors || []).filter((e) => e.startsWith(`${field}:`));
    const cellWarns = (row._warnings || []).filter((w) => w.startsWith(`${field}:`));
    const cls =
        `cu-cell` +
        (cellErrors.length ? " has-error" : "") +
        (!cellErrors.length && cellWarns.length ? " has-warn" : "") +
        (override !== undefined ? " is-edited" : "");
    return (
        <td className={cls} data-testid={`cu-cell-${rowIndex}-${field}`}>
            <input
                type="text"
                className="cu-cell-input"
                value={displayVal == null ? "" : String(displayVal)}
                onChange={(e) => onEdit(e.target.value)}
                data-testid={`cu-input-${rowIndex}-${field}`}
            />
            {cellErrors.length > 0 && (
                <div className="cu-cell-msg cu-cell-err" title={cellErrors.join(" / ")}>
                    {cellErrors[0].replace(`${field}:`, "").trim()}
                </div>
            )}
            {cellErrors.length === 0 && cellWarns.length > 0 && (
                <div className="cu-cell-msg cu-cell-warn" title={cellWarns.join(" / ")}>
                    {cellWarns[0].replace(`${field}:`, "").trim()}
                </div>
            )}
        </td>
    );
}

function RowStatus({ row }) {
    const { t } = useTranslation();
    const errCount = (row._errors || []).length;
    const warnCount = (row._warnings || []).length;
    const dup = row._duplicate_of;
    if (errCount > 0) {
        return <span className="cu-status cu-status-err">{t("admin.census.review.status.error", { n: errCount })}</span>;
    }
    if (dup?.source === "intra_file") {
        return <span className="cu-status cu-status-dup">{t("admin.census.review.status.dupIntra")}</span>;
    }
    if (dup?.source === "existing_roster") {
        return <span className="cu-status cu-status-dup">{t("admin.census.review.status.dupCross")}</span>;
    }
    if (warnCount > 0) {
        return <span className="cu-status cu-status-warn">{t("admin.census.review.status.warn", { n: warnCount })}</span>;
    }
    return <span className="cu-status cu-status-ok">{t("admin.census.review.status.ok")}</span>;
}

function ActionPicker({ rowIndex, current, hasErrors, isCross, isIntra, onChange }) {
    const { t } = useTranslation();
    // Available actions depend on row state.
    const opts = [];
    if (!isCross) opts.push("include");
    if (isCross) opts.push("overwrite");
    opts.push("exclude");
    // If row has errors, force include/overwrite to only show if user fixes inline.
    return (
        <div className="cu-action-picker" role="group" data-testid={`cu-action-${rowIndex}`}>
            {opts.map((a) => (
                <button
                    key={a}
                    type="button"
                    className={`cu-action-pill cu-action-${a}${current === a ? " is-on" : ""}`}
                    onClick={() => onChange(a)}
                    disabled={hasErrors && (a === "include" || a === "overwrite")}
                    data-testid={`cu-action-${a}-${rowIndex}`}
                >
                    {t(`admin.census.review.action.${a}`)}
                </button>
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Result summary (Step 4) + live 30s undo countdown.
 * -------------------------------------------------------------------- */
function CommitResult({ commit, applicationId, onDoneAnother, onClose, onUndone }) {
    const { t } = useTranslation();
    const [secondsLeft, setSecondsLeft] = useState(() => {
        if (!commit?.undo_expires_at) return 0;
        const ms = new Date(commit.undo_expires_at).getTime() - Date.now();
        return Math.max(0, Math.ceil(ms / 1000));
    });
    const [undoState, setUndoState] = useState("idle"); // idle | undoing | undone | expired | failed
    const [undoCount, setUndoCount] = useState(0);
    const [undoErr, setUndoErr] = useState(null);

    useEffect(() => {
        if (!commit?.undo_expires_at) return undefined;
        if (undoState === "undone" || undoState === "failed") return undefined;
        const id = setInterval(() => {
            const ms = new Date(commit.undo_expires_at).getTime() - Date.now();
            const s = Math.max(0, Math.ceil(ms / 1000));
            setSecondsLeft(s);
            if (s <= 0) {
                setUndoState((prev) => (prev === "idle" ? "expired" : prev));
            }
        }, 500);
        return () => clearInterval(id);
    }, [commit?.undo_expires_at, undoState]);

    const doUndo = async () => {
        if (!commit?.upload_id) return;
        setUndoState("undoing");
        setUndoErr(null);
        try {
            const res = await censusUndoCommit(commit.upload_id);
            setUndoCount(res?.cancelled || 0);
            setUndoState("undone");
            track("admin_census_commit_undone", {
                applicationId,
                upload_id: commit.upload_id,
                cancelled: res?.cancelled || 0,
            });
            if (onUndone) onUndone();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 410) {
                setUndoState("expired");
                setUndoErr(detail || t("admin.census.done.undoExpired"));
            } else {
                setUndoState("failed");
                setUndoErr(detail || t("admin.census.done.undoFailed"));
            }
        }
    };

    const showUndo = !!commit?.undo_expires_at && (commit.appended + commit.overwritten > 0);
    const pct = showUndo
        ? Math.max(0, Math.min(100, (secondsLeft / 30) * 100))
        : 0;

    return (
        <div className="cu-done" data-testid="census-done">
            <div className="cu-step-head">
                <div className="cu-step-label mono">{t("admin.census.done.label")}</div>
                <h4>{t("admin.census.done.title")}</h4>
                <p className="cu-step-sub">
                    {undoState === "undone"
                        ? t("admin.census.done.undoneSub", { n: undoCount })
                        : t("admin.census.done.sub")}
                </p>
            </div>
            <div className="cu-done-grid">
                <div className="cu-done-card">
                    <div className="cu-done-num">{undoState === "undone" ? 0 : commit.appended}</div>
                    <div className="cu-done-label mono">{t("admin.census.done.appended")}</div>
                </div>
                <div className="cu-done-card">
                    <div className="cu-done-num">{undoState === "undone" ? 0 : commit.overwritten}</div>
                    <div className="cu-done-label mono">{t("admin.census.done.overwritten")}</div>
                </div>
                <div className="cu-done-card">
                    <div className="cu-done-num">{commit.skipped}</div>
                    <div className="cu-done-label mono">{t("admin.census.done.skipped")}</div>
                </div>
                <div className={`cu-done-card${(commit.errors || []).length ? " has-error" : ""}`}>
                    <div className="cu-done-num">{(commit.errors || []).length}</div>
                    <div className="cu-done-label mono">{t("admin.census.done.errors")}</div>
                </div>
            </div>

            {showUndo && (
                <div className="cu-undo-strip" data-testid="cu-undo-strip">
                    <div className="cu-undo-bar">
                        <div className="cu-undo-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="cu-undo-row">
                        <span className="cu-undo-label mono" data-testid="cu-undo-label">
                            {undoState === "undone"
                                ? t("admin.census.done.undoDone", { n: undoCount })
                                : undoState === "expired" || secondsLeft <= 0
                                    ? t("admin.census.done.undoClosed")
                                    : undoState === "undoing"
                                        ? t("admin.census.done.undoing")
                                        : t("admin.census.done.undoCountdown", { s: secondsLeft })}
                        </span>
                        <button
                            type="button"
                            className="admin-btn primary cu-undo-btn"
                            onClick={doUndo}
                            disabled={
                                undoState !== "idle"
                                || secondsLeft <= 0
                            }
                            data-testid="cu-undo-btn"
                        >
                            {undoState === "undone"
                                ? t("admin.census.done.undoneCta")
                                : secondsLeft <= 0 || undoState === "expired"
                                    ? t("admin.census.done.sentCta")
                                    : t("admin.census.done.undoCta")}
                        </button>
                    </div>
                    {undoErr && (
                        <div className="review-err" data-testid="cu-undo-err" style={{ marginTop: "0.5rem" }}>
                            {undoErr}
                        </div>
                    )}
                </div>
            )}

            {(commit.errors || []).length > 0 && (
                <details style={{ marginTop: "1rem" }} data-testid="cu-done-errors">
                    <summary>{t("admin.census.done.errorDetails")}</summary>
                    <ul className="batch-invite-errors">
                        {(commit.errors || []).map((e, i) => (
                            <li key={i}>{e.email || "(row)"} — {e.reason}</li>
                        ))}
                    </ul>
                </details>
            )}
            <div className="cu-step-actions">
                <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={onDoneAnother}
                    data-testid="cu-done-another"
                >
                    {t("admin.census.done.another")}
                </button>
                <button
                    type="button"
                    className="admin-btn primary"
                    onClick={onClose}
                    data-testid="cu-done-close"
                >
                    {t("admin.census.done.close")}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Upload flow container.
 * -------------------------------------------------------------------- */
function UploadFlow({ applicationId, onDone }) {
    const { t } = useTranslation();
    // phase: idle | mapping | review | done
    const [phase, setPhase] = useState("idle");
    const [parseRes, setParseRes] = useState(null);
    const [mapRes, setMapRes] = useState(null);
    const [mapping, setMapping] = useState({});                // internal editable copy
    const [rememberMapping, setRememberMapping] = useState(true);
    const [validateRes, setValidateRes] = useState(null);
    const [overrides, setOverrides] = useState({});            // { [rowIdx]: { field: value } }
    const [actions, setActions] = useState({});                // { [rowIdx]: action }
    const [commitRes, setCommitRes] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [mapping_loading, setMappingLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [err, setErr] = useState(null);

    const reset = useCallback(() => {
        setPhase("idle");
        setParseRes(null);
        setMapRes(null);
        setMapping({});
        setValidateRes(null);
        setOverrides({});
        setActions({});
        setCommitRes(null);
        setErr(null);
    }, []);

    const handleFile = async (file, fileErr) => {
        setErr(fileErr);
        if (!file) return;
        setParsing(true);
        track("admin_census_upload_start", { applicationId, filename: file.name, size: file.size });
        try {
            const res = await censusParse(applicationId, file);
            setParseRes(res);
            track("admin_census_upload_parsed", {
                applicationId,
                upload_id: res.upload_id,
                row_count: res.row_count,
                header_count: (res.headers || []).length,
            });
            setPhase("mapping");
            setMappingLoading(true);
            try {
                const m = await censusMap(res.upload_id, false);
                setMapRes(m);
                setMapping(m.mapping || {});
                track("admin_census_map_received", {
                    applicationId,
                    upload_id: res.upload_id,
                    auto_applied: !!m.auto_applied,
                });
            } catch (e) {
                const d = e?.response?.data?.detail;
                setErr(typeof d === "object" ? d?.message || t("admin.census.errors.mapFailed") : d || t("admin.census.errors.mapFailed"));
                track("admin_census_map_failed", { applicationId });
            } finally {
                setMappingLoading(false);
            }
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || t("admin.census.errors.parseFailed") : d || t("admin.census.errors.parseFailed"));
            track("admin_census_upload_failed", { applicationId });
        } finally {
            setParsing(false);
        }
    };

    const setMappingField = (header, canonical) => {
        setMapping((prev) => ({
            ...prev,
            [header]: {
                ...(prev[header] || {}),
                canonical_field: canonical || null,
                // user-chosen mapping is treated as high-confidence
                confidence: canonical ? 1 : 0,
                reason: canonical ? t("admin.census.map.userChosen") : t("admin.census.map.userSkipped"),
            },
        }));
    };

    const forceRemap = async () => {
        if (!parseRes?.upload_id) return;
        setMappingLoading(true);
        setErr(null);
        try {
            const m = await censusMap(parseRes.upload_id, true);
            setMapRes(m);
            setMapping(m.mapping || {});
            track("admin_census_map_received", { applicationId, upload_id: parseRes.upload_id, auto_applied: false, forced: true });
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || t("admin.census.errors.mapFailed") : d || t("admin.census.errors.mapFailed"));
        } finally {
            setMappingLoading(false);
        }
    };

    const continueToReview = async () => {
        // Build mapping payload = only header → canonical (strings)
        const payload = {};
        Object.entries(mapping).forEach(([h, entry]) => {
            if (entry?.canonical_field) payload[h] = entry.canonical_field;
        });
        setValidating(true);
        setErr(null);
        try {
            const res = await censusValidate(parseRes.upload_id, payload, rememberMapping, {});
            setValidateRes(res);
            // Seed default actions
            const defAct = {};
            (res.rows || []).forEach((r, i) => {
                defAct[i] = inferDefaultAction(r, i, res);
            });
            setActions(defAct);
            setOverrides({});
            setPhase("review");
            track("admin_census_validated", {
                applicationId,
                upload_id: parseRes.upload_id,
                total: res.summary?.total,
                valid: res.summary?.valid,
                errors: res.summary?.with_errors,
                warnings: res.summary?.with_warnings,
                duplicates: res.summary?.duplicates,
            });
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || t("admin.census.errors.validateFailed") : d || t("admin.census.errors.validateFailed"));
            track("admin_census_validate_failed", { applicationId });
        } finally {
            setValidating(false);
        }
    };

    const setOverride = (idx, field, value) => {
        setOverrides((prev) => {
            const next = { ...prev };
            const cur = { ...(next[idx] || {}) };
            // If same as original and user cleared → remove
            const orig = validateRes?.rows?.[idx]?.[field];
            if (String(orig ?? "") === String(value ?? "")) {
                delete cur[field];
            } else {
                cur[field] = value;
            }
            if (Object.keys(cur).length === 0) delete next[idx];
            else next[idx] = cur;
            return next;
        });
    };

    const setAction = (idx, a) => setActions((prev) => ({ ...prev, [idx]: a }));

    const revalidate = async () => {
        if (!parseRes?.upload_id) return;
        const payload = {};
        Object.entries(mapping).forEach(([h, entry]) => {
            if (entry?.canonical_field) payload[h] = entry.canonical_field;
        });
        const ov = {};
        Object.entries(overrides).forEach(([idx, fields]) => {
            ov[String(idx)] = fields;
        });
        setValidating(true);
        setErr(null);
        try {
            const res = await censusValidate(parseRes.upload_id, payload, false, ov);
            setValidateRes(res);
            // Re-infer default action per row (respect user's prior picks where still valid)
            setActions((prev) => {
                const next = {};
                (res.rows || []).forEach((r, i) => {
                    const prevA = prev[i];
                    const inferred = inferDefaultAction(r, i, res);
                    // If user previously picked something that's still plausible, keep it;
                    // else reset to inferred.
                    next[i] = prevA || inferred;
                });
                return next;
            });
            track("admin_census_revalidated", { applicationId, upload_id: parseRes.upload_id });
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || t("admin.census.errors.validateFailed") : d || t("admin.census.errors.validateFailed"));
        } finally {
            setValidating(false);
        }
    };

    const doCommit = async () => {
        if (!parseRes?.upload_id) return;
        const payload = {};
        Object.entries(mapping).forEach(([h, entry]) => {
            if (entry?.canonical_field) payload[h] = entry.canonical_field;
        });
        const ov = {};
        Object.entries(overrides).forEach(([idx, fields]) => {
            ov[String(idx)] = fields;
        });
        const rowsPayload = (validateRes?.rows || []).map((r, i) => ({
            index: i,
            action: actions[i] || inferDefaultAction(r, i, validateRes),
        }));
        setCommitting(true);
        setErr(null);
        try {
            const res = await censusCommit(parseRes.upload_id, payload, rowsPayload, ov);
            setCommitRes(res);
            setPhase("done");
            track("admin_census_committed", {
                applicationId,
                upload_id: parseRes.upload_id,
                appended: res.appended,
                overwritten: res.overwritten,
                skipped: res.skipped,
                errors: (res.errors || []).length,
            });
            if ((res.appended || 0) + (res.overwritten || 0) > 0 && onDone) onDone();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || t("admin.census.errors.commitFailed") : d || t("admin.census.errors.commitFailed"));
            track("admin_census_commit_failed", { applicationId });
        } finally {
            setCommitting(false);
        }
    };

    return (
        <div className="cu-upload" data-testid="cu-upload">
            {phase === "idle" && (
                <DropZone onFile={handleFile} busy={parsing} error={err} />
            )}

            {phase === "mapping" && parseRes && (
                <>
                    <div className="cu-meta-strip mono" data-testid="cu-meta-strip">
                        <span>{parseRes.filename}</span>
                        <span>·</span>
                        <span>{parseRes.row_count} {t("admin.census.meta.rows")}</span>
                        <span>·</span>
                        <span>{(parseRes.headers || []).length} {t("admin.census.meta.cols")}</span>
                        <span>·</span>
                        <span>{Math.round((parseRes.size_bytes || 0) / 1024)} KB</span>
                    </div>
                    {Array.isArray(parseRes.ssn_blocked_columns) && parseRes.ssn_blocked_columns.length > 0 && (
                        <div className="cu-ssn-banner" data-testid="cu-ssn-banner">
                            <div className="cu-ssn-banner-title mono">
                                {t("admin.census.ssn.bannerTitle")}
                            </div>
                            <div className="cu-ssn-banner-body">
                                {t("admin.census.ssn.bannerBody")}
                            </div>
                            <div className="cu-ssn-banner-cols mono">
                                {t("admin.census.ssn.columnsLabel")}:{" "}
                                {parseRes.ssn_blocked_columns.map((c) => (
                                    <code key={c}>{c}</code>
                                )).reduce((acc, el, i) => (
                                    i === 0 ? [el] : [...acc, ", ", el]
                                ), [])}
                            </div>
                        </div>
                    )}
                    {mapping_loading && !mapRes && (
                        <div className="cu-loading" data-testid="cu-map-loading">
                            <div className="cu-spinner" />
                            <div>{t("admin.census.map.thinking")}</div>
                        </div>
                    )}
                    {mapRes && (
                        <MappingGrid
                            parse={parseRes}
                            mapping={mapping}
                            canonicalSchema={mapRes.canonical_schema || {}}
                            autoApplied={!!mapRes.auto_applied}
                            onChange={setMappingField}
                            rememberMapping={rememberMapping}
                            setRememberMapping={setRememberMapping}
                            onContinue={continueToReview}
                            onBack={reset}
                            onRemap={forceRemap}
                            validating={validating}
                        />
                    )}
                    {err && <div className="review-err" data-testid="cu-phase-err">{err}</div>}
                </>
            )}

            {phase === "review" && validateRes && (
                <>
                    <ReviewGrid
                        validate={validateRes}
                        overrides={overrides}
                        setOverride={setOverride}
                        actions={actions}
                        setAction={setAction}
                        onRevalidate={revalidate}
                        onCommit={doCommit}
                        onBack={() => setPhase("mapping")}
                        revalidating={validating}
                        committing={committing}
                    />
                    {err && <div className="review-err" data-testid="cu-phase-err">{err}</div>}
                </>
            )}

            {phase === "done" && commitRes && (
                <CommitResult
                    commit={commitRes}
                    applicationId={applicationId}
                    onDoneAnother={reset}
                    onClose={reset}
                    onUndone={() => {
                        // After an undo, drop the user back into the review
                        // grid so they can adjust and commit again.
                        if (validateRes) {
                            setPhase("review");
                        } else {
                            reset();
                        }
                    }}
                />
            )}
        </div>
    );
}

/* -------------------------------------------------------------------- *
 * Top-level panel — swaps in place of the old BatchInvitePanel.
 * -------------------------------------------------------------------- */
export default function CensusUploadPanel({ applicationId, status, onDone }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState("upload");
    const banner =
        status === "enrollment_open"
            ? t("admin.census.bannerOpen")
            : t("admin.census.bannerIssued");

    const changeTab = (next) => {
        if (next === tab) return;
        setTab(next);
        track("admin_census_tab_change", { applicationId, tab: next });
    };

    return (
        <div className="batch-invite-panel cu-panel" data-testid="batch-invite-panel">
            <h4>{t("admin.census.panelTitle")}</h4>
            <div className="batch-help">{banner}</div>
            <TabBar active={tab} onChange={changeTab} />
            {tab === "upload" && (
                <UploadFlow applicationId={applicationId} onDone={onDone} />
            )}
            {tab === "paste" && (
                <PasteFallback applicationId={applicationId} onDone={onDone} />
            )}
        </div>
    );
}
